import { TourLifecycleState, getTourLifecycleMarkMetadata } from 'onboarding-tours-utils/lifecycleCore';
import { getCurrentTourId } from './shouldRenderTour';

/**
 * Get the current tour lifecycle state and tour ID from performance marks.
 * Returns INIT if no marks exist and getCurrentTourId() returns a tour ID (library should load but hasn't yet).
 * Returns NO_TOUR if no marks exist and getCurrentTourId() returns null (page has no tour).
 */
export function getTourLifecycleDetails() {
  const lifecycleMarkMetadata = getTourLifecycleMarkMetadata();
  if (!lifecycleMarkMetadata) {
    const tourId = getCurrentTourId();
    return tourId ? {
      state: TourLifecycleState.INIT,
      tourId
    } : {
      state: TourLifecycleState.NO_TOUR,
      tourId: null
    };
  }
  return lifecycleMarkMetadata;
}

/**
 * Check if user is currently in an active tour session.
 * Returns true if the state indicates an ongoing tour (not NO_TOUR, CONFIG_LOAD_ERROR, TOUR_LOAD_CANCELLED, or TOUR_ENDED).
 * Can be used independently across different entries.
 *
 * @param tourId - Optional tour ID to check if the user is in a specific tour session
 */
export function isInTourSession(tourId) {
  const {
    state,
    tourId: currentTourId
  } = getTourLifecycleDetails();
  const nonSessionStates = [TourLifecycleState.NO_TOUR, TourLifecycleState.CONFIG_LOAD_ERROR, TourLifecycleState.TOUR_LOAD_CANCELLED, TourLifecycleState.TOUR_ENDED];
  const isInSession = !nonSessionStates.includes(state);
  return tourId ? isInSession && currentTourId === tourId : isInSession;
}

/**
 * Check if a tour is currently active (in loading or active state).
 * Returns true if the state indicates the tour is loading or active.
 * Different from isInTourSession - excludes error/cancelled states.
 *
 * @param tourId - Optional tour ID to check if a specific tour is active
 */
export function isTourCurrentlyActive(tourId) {
  const lifecycleMarkMetadata = getTourLifecycleMarkMetadata();
  if (!lifecycleMarkMetadata) {
    return false;
  }
  const activeStatus = [TourLifecycleState.CONFIG_LOADED, TourLifecycleState.TOUR_STARTING, TourLifecycleState.TOUR_ACTIVE];
  const {
    state,
    tourId: currentTourId
  } = lifecycleMarkMetadata;
  const isTourActive = activeStatus.includes(state);
  return tourId ? isTourActive && currentTourId === tourId : isTourActive;
}